import { useMemo, useState, useEffect } from "react";
import { useCrm } from "@/lib/crm";
import { useAuth } from "@/lib/auth";
import { eur, eur2, dateIt, compensoSsd, sortDates, rataIndex, MONTHLY_TARGETS, STAGES, bonusCount, bonusUnit, BONUS_TIPI, CANALI, isAcq } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trophy, Wallet, Target, CalendarClock, Save, Mail, Phone, FileSpreadsheet, Percent, CalendarRange, History, ArrowRight, KanbanSquare, MapPin, UserPlus, Trash2, Pencil, CalendarSync } from "lucide-react";

export default function Collaboratori() {
  const { ssd, collaboratori, settings, api, refresh, setSettings } = useCrm();
  const { user, isTitolare } = useAuth();
  const comm = settings?.commissione ?? 0.5;
  const target = settings?.bonus_target_ssd ?? 15;
  const isRitr = settings?.settore === "ritrattistica";
  const entLabel = isRitr ? "Clienti" : "Società";
  const unit = bonusUnit(settings);
  const [pipelineOf, setPipelineOf] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ nome: "", telefono: "", commissione: 50, colore: "#EA580C" });
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (c) => {
    setEditId(c.id);
    setEditForm({ nome: c.nome || "", telefono: c.telefono || "", commissione: Math.round((c.commissione ?? comm) * 100), colore: c.colore || "#EA580C" });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!editForm.nome.trim()) { toast.error("Il nome è obbligatorio"); return; }
    setEditSaving(true);
    try {
      await api.updateCollab(editId, {
        nome: editForm.nome.trim(),
        telefono: editForm.telefono.trim(),
        colore: editForm.colore,
        commissione: (Math.max(0, Math.min(100, Number(editForm.commissione) || 0))) / 100,
      });
      await refresh();
      setEditOpen(false);
      toast.success("Dati collaboratore aggiornati");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setEditSaving(false);
    }
  };
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ nome: "", email: "", password: "", telefono: "", commissione: 50, colore: "#EA580C" });
  const [addSaving, setAddSaving] = useState(false);

  const createCollab = async () => {
    if (!addForm.nome.trim()) { toast.error("Inserisci il nome del collaboratore"); return; }
    if (!addForm.email.trim() || !addForm.password) { toast.error("Email e password sono obbligatorie"); return; }
    if (addForm.password.length < 4) { toast.error("La password deve avere almeno 4 caratteri"); return; }
    setAddSaving(true);
    try {
      await api.createCollab({
        nome: addForm.nome.trim(),
        email: addForm.email.trim(),
        password: addForm.password,
        telefono: addForm.telefono.trim(),
        colore: addForm.colore,
        commissione: (Math.max(0, Math.min(100, Number(addForm.commissione) || 0))) / 100,
      });
      await refresh();
      setAddOpen(false);
      setAddForm({ nome: "", email: "", password: "", telefono: "", commissione: 50, colore: "#EA580C" });
      toast.success("Collaboratore creato. Gli è stata inviata anche l'email per collegare il calendario.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella creazione del collaboratore");
    } finally {
      setAddSaving(false);
    }
  };

  const inviaCal = async (c) => {
    try {
      const r = await api.inviaCalendario(c.id);
      toast.success(`Email con il calendario inviata a ${r.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    }
  };
  const inviaCalTutti = async () => {
    if (!window.confirm("Inviare a tutti i collaboratori di questo settore l'email con il loro link del calendario?")) return;
    try {
      const r = await api.inviaCalendarioTutti();
      if (r.errori?.length) toast.warning(`Inviate ${r.inviati} email, non riuscite: ${r.errori.join(", ")}`);
      else toast.success(`Email inviata a ${r.inviati} collaboratori`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    }
  };

  const removeCollab = async (c) => {
    if (!window.confirm(`Eliminare il collaboratore ${c.nome}? ${isRitr ? "I clienti assegnati" : "Le società assegnate"} resteranno in pipeline senza collaboratore.`)) return;
    try {
      await api.deleteCollab(c.id);
      await refresh();
      toast.success("Collaboratore eliminato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const rows = useMemo(() => {
    const acquired = ssd.filter((s) => isAcq(s, settings));
    return collaboratori
      .filter((c) => isTitolare || c.id === user?.collaboratore_id)
      .map((c) => {
      const assigned = ssd.filter((s) => s.collaboratore_id === c.id);
      const acq = acquired.filter((s) => s.collaboratore_id === c.id);
      const rev = acq.reduce((a, s) => a + (s.ricavo || 0), 0);
      const share = acq.reduce((a, s) => a + compensoSsd(s, c, comm, settings), 0);
      const progress = bonusCount(acq, settings);
      return { c, assigned: assigned.length, acquired: acq.length, progress, rev, share, bonus: target > 0 && progress >= target };
    });
  }, [ssd, collaboratori, comm, target, isTitolare, user, settings]);

  const sortedDates = useMemo(() => sortDates(settings?.date_liquidazione), [settings]);
  const bonusImporto = settings?.bonus_importo ?? 1000;
  const bonusScad = settings?.bonus_scadenza || "";

  const rateRows = useMemo(() => {
    const acquired = ssd.filter((s) => isAcq(s, settings));
    const bonusRata = rataIndex(bonusScad, sortedDates);
    return collaboratori
      .filter((c) => isTitolare || c.id === user?.collaboratore_id)
      .map((c) => {
        const acq = acquired.filter((s) => s.collaboratore_id === c.id);
        const rate = [0, 0, 0];
        acq.forEach((s) => {
          const tipo = s.compenso_tipo || "default";
          const lavori = (s.lavori && s.lavori.length) ? s.lavori : null;
          if (lavori && tipo !== "fisso") {
            const rate1 = tipo === "percentuale" ? (Number(s.compenso_valore) || 0) : (c && c.commissione != null ? c.commissione : comm);
            lavori.forEach((l) => {
              const idx = rataIndex(l.data || s.data_acquisizione, sortedDates);
              rate[idx] = (rate[idx] || 0) + (Number(l.importo) || 0) * rate1;
            });
          } else {
            const idx = rataIndex(s.data_acquisizione, sortedDates);
            rate[idx] = (rate[idx] || 0) + compensoSsd(s, c, comm, settings);
          }
        });
        const bonus = target > 0 && bonusCount(acq, settings) >= target ? bonusImporto : 0;
        if (bonus) rate[bonusRata] = (rate[bonusRata] || 0) + bonus;
        const total = rate.reduce((a, x) => a + x, 0);
        return { c, rate, bonus, total };
      });
  }, [ssd, collaboratori, comm, target, sortedDates, bonusScad, bonusImporto, isTitolare, user, settings]);

  const rateTotals = useMemo(() => {
    const t = [0, 0, 0];
    let tot = 0;
    rateRows.forEach((r) => { r.rate.forEach((v, i) => { t[i] += v; }); tot += r.total; });
    return { t, tot };
  }, [rateRows]);

  const [storico, setStorico] = useState([]);
  useEffect(() => {
    if (isTitolare) api.listCompensiStorico().then(setStorico).catch(() => setStorico([]));
  }, [isTitolare, api]);

  const [dates, setDates] = useState(settings?.date_liquidazione || ["", "", ""]);
  useEffect(() => { if (settings) setDates(settings.date_liquidazione); }, [settings]);

  const [pct, setPct] = useState({});
  useEffect(() => {
    const m = {};
    collaboratori.forEach((c) => { m[c.id] = Math.round((c.commissione ?? comm) * 100); });
    setPct(m);
  }, [collaboratori, comm]);

  const savePct = async (c) => {
    const val = Math.max(0, Math.min(100, Number(pct[c.id]) || 0)) / 100;
    await api.updateCollab(c.id, { commissione: val });
    await refresh();
    if (isTitolare) api.listCompensiStorico().then(setStorico).catch(() => {});
    toast.success(`Percentuale di ${c.nome.split(" ")[0]} aggiornata a ${Math.round(val * 100)}%`);
  };

  const saveDates = async () => {
    const s = await api.updateSettings({ date_liquidazione: dates });
    setSettings(s);
    toast.success("Date di liquidazione aggiornate");
  };

  const [obiettivi, setObiettivi] = useState({ importo: 1000, target: 15, scadenza: "", tipo: "clienti" });
  const [mensili, setMensili] = useState(MONTHLY_TARGETS);
  useEffect(() => {
    if (settings) {
      setObiettivi({ importo: settings.bonus_importo ?? 1000, target: settings.bonus_target_ssd ?? 15, scadenza: settings.bonus_scadenza || "", tipo: settings.bonus_tipo || "clienti" });
      setMensili(settings.target_mensili?.length ? settings.target_mensili : MONTHLY_TARGETS);
    }
  }, [settings]);

  const [pctCanali, setPctCanali] = useState({});
  useEffect(() => {
    if (settings) setPctCanali(Object.fromEntries(CANALI.map((c) => [c.key, Math.round((settings.commissioni_canale?.[c.key] ?? 0.5) * 100)])));
  }, [settings]);
  const savePctCanali = async () => {
    const s = await api.updateSettings({ commissioni_canale: Object.fromEntries(CANALI.map((c) => [c.key, Math.max(0, Math.min(100, Number(pctCanali[c.key]) || 0)) / 100])) });
    setSettings(s);
    toast.success("Percentuali per canale aggiornate");
  };

  const saveObiettivi = async () => {
    const s = await api.updateSettings({
      bonus_importo: Number(obiettivi.importo) || 0,
      bonus_target_ssd: Number(obiettivi.target) || 0,
      bonus_scadenza: obiettivi.scadenza,
      ...(isRitr ? { bonus_tipo: obiettivi.tipo } : {}),
      target_mensili: mensili.map((m) => ({ ...m, target: Number(m.target) || 0 })),
    });
    setSettings(s);
    toast.success("Obiettivi e bonus aggiornati");
  };

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">Collaboratori & Compensi</h1>
          <p className="text-slate-500 mt-1">Percentuale variabile per collaboratore, con eventuali override sui singoli {isRitr ? "clienti" : "SSD"}.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <Button
          data-testid="export-compensi-button"
          variant="outline"
          className="rounded-full gap-1.5"
          style={{ display: isTitolare ? undefined : "none" }}
          onClick={() => api.exportCompensi().then(() => toast.success("Export compensi generato")).catch(() => toast.error("Errore durante l'export"))}
        >
          <FileSpreadsheet className="h-4 w-4" /> Esporta Compensi (Excel)
        </Button>
        {isTitolare && (
          <Button data-testid="invia-calendario-tutti-button" variant="outline" className="rounded-full gap-1.5" onClick={inviaCalTutti}>
            <CalendarSync className="h-4 w-4" /> Invia link calendario a tutti
          </Button>
        )}
        {isTitolare && (
          <Button data-testid="add-collaborator-button" className="rounded-full gap-1.5" onClick={() => setAddOpen(true)}>
            <UserPlus className="h-4 w-4" /> Aggiungi collaboratore
          </Button>
        )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {rows.map(({ c, assigned, acquired, progress, rev, share, bonus }, i) => (
          <Card key={c.id} data-testid={`collaborator-${i + 1}-card`} className="p-6 animate-fade-up">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-12 w-12 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-sm" style={{ background: c.colore }}>
                {c.nome.split(" ").map((n) => n[0]).slice(0, 2).join("")}
              </div>
              <div>
                <div className="font-bold text-lg text-slate-900 dark:text-white">{c.nome}</div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mt-0.5">
                  {c.telefono
                    ? <a data-testid={`collaborator-${i + 1}-telefono`} href={`tel:${c.telefono.replace(/\s/g, "")}`} className="inline-flex items-center gap-1 hover:text-primary"><Phone className="h-3 w-3" />{c.telefono}</a>
                    : <span data-testid={`collaborator-${i + 1}-telefono`} className="inline-flex items-center gap-1 text-slate-400"><Phone className="h-3 w-3" />Telefono non inserito</span>}
                  {c.email && <a data-testid={`collaborator-${i + 1}-email`} href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-primary"><Mail className="h-3 w-3" />{c.email}</a>}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="rounded-xl bg-slate-50 dark:bg-slate-900/50 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">In pipeline</div>
                <div className="text-2xl font-extrabold text-slate-900 dark:text-white tabular-nums">{assigned}</div>
              </div>
              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-500/10 p-3">
                <div className="text-[10px] uppercase tracking-wider text-emerald-600/80">{isRitr ? "Acquisiti" : "Acquisite"}</div>
                <div className="text-2xl font-extrabold text-emerald-600 tabular-nums">{acquired}</div>
              </div>
              <div className="rounded-xl bg-primary/10 p-3">
                <div className="text-[10px] uppercase tracking-wider text-primary/80">Ricavi</div>
                <div className="text-2xl font-extrabold text-primary tabular-nums">{eur(rev)}</div>
              </div>
            </div>

            <div className="flex items-end gap-3 rounded-xl border border-slate-200 dark:border-slate-800 p-4 mb-4">
              <div className="grid gap-1.5 w-28" style={{ display: isTitolare && !isRitr ? undefined : "none" }}>
                <Label className="text-xs flex items-center gap-1"><Percent className="h-3 w-3" /> Percentuale</Label>
                <div className="relative">
                  <Input data-testid={`collaborator-${i + 1}-pct-input`} type="number" min="0" max="100"
                    value={pct[c.id] ?? ""} onChange={(e) => setPct((p) => ({ ...p, [c.id]: e.target.value }))}
                    className="pr-6 tabular-nums" />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                </div>
              </div>
              {isTitolare && !isRitr && <Button data-testid={`collaborator-${i + 1}-save-pct`} variant="outline" size="sm" className="rounded-full h-9" onClick={() => savePct(c)}>Salva</Button>}
              {isRitr && (
                <div className="flex gap-3" data-testid={`collaborator-${i + 1}-pct-canali`}>
                  {CANALI.map((ch) => (
                    <div key={ch.key}>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400">{ch.label.replace("Clienti ", "").replace("Portrait ", "")}</div>
                      <span className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">{Math.round((settings?.commissioni_canale?.[ch.key] ?? comm) * 100)}%</span>
                    </div>
                  ))}
                </div>
              )}
              {!isTitolare && !isRitr && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Percentuale</div>
                  <span className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">{Math.round((c.commissione ?? comm) * 100)}%</span>
                </div>
              )}
              <div className="ml-auto text-right">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1 justify-end"><Wallet className="h-3 w-3" /> Compenso</div>
                <span data-testid={`collaborator-${i + 1}-share`} className="text-xl font-extrabold text-slate-900 dark:text-white tabular-nums">{eur2(share)}</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm flex items-center gap-1.5 text-slate-600 dark:text-slate-300"><Trophy className="h-4 w-4 text-amber-500" /> Bonus {eur(settings?.bonus_importo || 1000)}</span>
                {bonus ? <Badge className="bg-emerald-500 hover:bg-emerald-500">Raggiunto</Badge> : <span data-testid={`collaborator-${i + 1}-bonus-progress`} className="text-xs text-slate-500 tabular-nums">{progress}/{target} {unit}</span>}
              </div>
              <Progress value={Math.min(100, (progress / (target || 1)) * 100)} className="h-2.5" />
            </div>

            <Button data-testid={`collaborator-${i + 1}-pipeline-btn`} variant="ghost" size="sm"
              className="mt-4 w-full rounded-full gap-1.5 text-slate-500 hover:text-primary border border-dashed border-slate-200 dark:border-slate-800"
              onClick={() => setPipelineOf(c)}>
              <KanbanSquare className="h-4 w-4" /> {entLabel} in pipeline ({assigned})
            </Button>
            {isTitolare && (
              <Button data-testid={`collaborator-${i + 1}-edit-btn`} variant="ghost" size="sm"
                className="mt-2 w-full rounded-full gap-1.5 text-slate-500 hover:text-primary border border-dashed border-slate-200 dark:border-slate-800"
                onClick={() => openEdit(c)}>
                <Pencil className="h-4 w-4" /> Modifica dati
              </Button>
            )}
            {isTitolare && (
              <Button data-testid={`collaborator-${i + 1}-invia-calendario-btn`} variant="ghost" size="sm"
                className="mt-2 w-full rounded-full gap-1.5 text-slate-500 hover:text-primary border border-dashed border-slate-200 dark:border-slate-800"
                onClick={() => inviaCal(c)}>
                <CalendarSync className="h-4 w-4" /> Invia link calendario
              </Button>
            )}
            {isTitolare && (
              <Button data-testid={`collaborator-${i + 1}-delete-btn`} variant="ghost" size="sm"
                className="mt-2 w-full rounded-full gap-1.5 text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
                onClick={() => removeCollab(c)}>
                <Trash2 className="h-4 w-4" /> Elimina collaboratore
              </Button>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={addOpen} onOpenChange={(o) => !o && setAddOpen(false)}>
        <DialogContent className="max-w-md" data-testid="add-collaborator-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-primary" /> Nuovo collaboratore</DialogTitle>
            <DialogDescription>Crea l'account: comunica poi email e password al collaboratore per l'accesso.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 pt-1">
            <div className="grid gap-1.5">
              <Label>Nome e cognome *</Label>
              <Input data-testid="new-collab-nome" value={addForm.nome} onChange={(e) => setAddForm((f) => ({ ...f, nome: e.target.value }))} placeholder="es. Marco Rossi" />
            </div>
            <div className="grid gap-1.5">
              <Label>Email di accesso *</Label>
              <Input data-testid="new-collab-email" type="email" value={addForm.email} onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} placeholder="nome@dominio.it" />
            </div>
            <div className="grid gap-1.5">
              <Label>Password *</Label>
              <Input data-testid="new-collab-password" type="text" value={addForm.password} onChange={(e) => setAddForm((f) => ({ ...f, password: e.target.value }))} placeholder="min. 4 caratteri" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label>Telefono</Label>
                <Input data-testid="new-collab-telefono" value={addForm.telefono} onChange={(e) => setAddForm((f) => ({ ...f, telefono: e.target.value }))} placeholder="+39 …" />
              </div>
              <div className="grid gap-1.5">
                <Label className="flex items-center gap-1"><Percent className="h-3 w-3" /> Commissione</Label>
                <div className="relative">
                  <Input data-testid="new-collab-pct" type="number" min="0" max="100" value={addForm.commissione} onChange={(e) => setAddForm((f) => ({ ...f, commissione: e.target.value }))} className="pr-6 tabular-nums" />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                </div>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label>Colore</Label>
              <input data-testid="new-collab-colore" type="color" value={addForm.colore} onChange={(e) => setAddForm((f) => ({ ...f, colore: e.target.value }))} className="h-10 w-20 rounded-lg border border-slate-200 dark:border-slate-800 cursor-pointer bg-transparent" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" className="rounded-full" onClick={() => setAddOpen(false)}>Annulla</Button>
              <Button data-testid="new-collab-save" className="rounded-full gap-1.5" onClick={createCollab} disabled={addSaving}>
                <Save className="h-4 w-4" /> {addSaving ? "Creazione…" : "Crea collaboratore"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={(o) => !o && setEditOpen(false)}>
        <DialogContent className="max-w-md" data-testid="edit-collaborator-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-primary" /> Modifica collaboratore</DialogTitle>
            <DialogDescription>Aggiorna i dati del collaboratore. L'email di accesso non è modificabile da qui.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 pt-1">
            <div className="grid gap-1.5">
              <Label>Nome e cognome</Label>
              <Input data-testid="edit-collab-nome" value={editForm.nome} onChange={(e) => setEditForm((f) => ({ ...f, nome: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label>Telefono</Label>
                <Input data-testid="edit-collab-telefono" value={editForm.telefono} onChange={(e) => setEditForm((f) => ({ ...f, telefono: e.target.value }))} />
              </div>
              <div className="grid gap-1.5">
                <Label className="flex items-center gap-1"><Percent className="h-3 w-3" /> Commissione</Label>
                <div className="relative">
                  <Input data-testid="edit-collab-pct" type="number" min="0" max="100" value={editForm.commissione} onChange={(e) => setEditForm((f) => ({ ...f, commissione: e.target.value }))} className="pr-6 tabular-nums" />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                </div>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label>Colore</Label>
              <input data-testid="edit-collab-colore" type="color" value={editForm.colore} onChange={(e) => setEditForm((f) => ({ ...f, colore: e.target.value }))} className="h-10 w-20 rounded-lg border border-slate-200 dark:border-slate-800 cursor-pointer bg-transparent" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" className="rounded-full" onClick={() => setEditOpen(false)}>Annulla</Button>
              <Button data-testid="edit-collab-save" className="rounded-full gap-1.5" onClick={saveEdit} disabled={editSaving}>
                <Save className="h-4 w-4" /> {editSaving ? "Salvataggio…" : "Salva modifiche"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pipelineOf} onOpenChange={(o) => !o && setPipelineOf(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {pipelineOf && <span className="h-6 w-6 rounded-lg flex items-center justify-center text-[10px] font-bold text-white" style={{ background: pipelineOf.colore }}>{pipelineOf.nome.split(" ").map((n) => n[0]).slice(0, 2).join("")}</span>}
              {entLabel} di {pipelineOf?.nome}
            </DialogTitle>
            <DialogDescription>Distribuzione nelle fasi della pipeline.</DialogDescription>
          </DialogHeader>
          {pipelineOf && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1" data-testid="collab-pipeline-dialog">
              {(isRitr ? [...(settings?.stages || []).map((x) => ({ ...x, grp: "privati" })), ...(settings?.stages_partner || []).map((x) => ({ ...x, grp: "partner", label: `Partner · ${x.label}` }))] : STAGES).map((st) => {
                const items = ssd.filter((x) => x.collaboratore_id === pipelineOf.id && x.stage === st.key && (!st.grp || (st.grp === "partner") === (x.canale === "partner")));
                if (items.length === 0) return null;
                return (
                  <div key={`${st.grp || ""}${st.key}`} data-testid={`collab-pipeline-stage-${st.grp ? st.grp + "-" : ""}${st.key}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`h-2.5 w-2.5 rounded-full ${st.dot || (st.key === "chiuso" ? "bg-emerald-500" : "bg-sky-500")}`} />
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{st.label}</span>
                      <span className="text-xs text-slate-400 tabular-nums">{items.length}</span>
                    </div>
                    <div className="space-y-1.5 pl-4">
                      {items.map((it) => (
                        <div key={it.id} className="flex items-center justify-between gap-2 text-sm rounded-lg border border-slate-100 dark:border-slate-800 px-3 py-2">
                          <span className="font-medium text-slate-800 dark:text-slate-100 truncate">{it.nome}</span>
                          <span className="text-xs text-slate-400 inline-flex items-center gap-2 shrink-0">
                            {it.citta && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{it.citta}</span>}
                            {isAcq(it, settings) && it.ricavo > 0 && <span className="text-primary font-semibold tabular-nums">{eur2(it.ricavo)}</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {ssd.filter((x) => x.collaboratore_id === pipelineOf.id).length === 0 && (
                <p className="text-sm text-slate-400 text-center py-6">{isRitr ? "Nessun cliente assegnato" : "Nessuna società assegnata"} a questo collaboratore.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Card className="p-6 animate-fade-up" data-testid="riepilogo-rate-card">
        <div className="flex items-center gap-2 mb-1">
          <CalendarRange className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Riepilogo Rate</h2>
        </div>
        <p className="text-xs text-slate-500 mb-5">Compensi ripartiti sulle 3 date di liquidazione, in base alla data di acquisizione di ogni cliente. Il bonus matura sulla rata che comprende la scadenza bonus.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800 text-left">
                <th className="px-3 py-2.5 font-semibold text-slate-500 uppercase text-[11px] tracking-wider">Collaboratore</th>
                {[0, 1, 2].map((i) => (
                  <th key={i} className="px-3 py-2.5 font-semibold text-slate-500 uppercase text-[11px] tracking-wider text-right">
                    Rata {i + 1}<div className="font-normal normal-case text-[10px] text-slate-400">{dateIt(sortedDates[i])}</div>
                  </th>
                ))}
                <th className="px-3 py-2.5 font-semibold text-slate-500 uppercase text-[11px] tracking-wider text-right">Totale</th>
              </tr>
            </thead>
            <tbody>
              {rateRows.map(({ c, rate, bonus, total }) => (
                <tr key={c.id} data-testid={`rate-row-${c.id}`} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="px-3 py-3">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{ background: c.colore }}>{c.nome.split(" ").map((n) => n[0]).slice(0, 2).join("")}</span>
                      <span className="text-slate-700 dark:text-slate-200 font-medium">{c.nome}</span>
                      {bonus > 0 && <Badge className="bg-amber-500 hover:bg-amber-500 text-[10px]">+ Bonus {eur(bonus)}</Badge>}
                    </span>
                  </td>
                  {rate.map((v, i) => (
                    <td key={i} className="px-3 py-3 text-right tabular-nums text-slate-700 dark:text-slate-200">{v > 0 ? eur2(v) : "—"}</td>
                  ))}
                  <td className="px-3 py-3 text-right font-bold text-primary tabular-nums">{eur2(total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 dark:bg-slate-900/50 font-bold">
                <td className="px-3 py-3 text-slate-900 dark:text-white">TOTALE</td>
                {rateTotals.t.map((v, i) => (
                  <td key={i} className="px-3 py-3 text-right tabular-nums text-slate-900 dark:text-white">{eur2(v)}</td>
                ))}
                <td className="px-3 py-3 text-right text-primary tabular-nums">{eur2(rateTotals.tot)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {isTitolare && isRitr && (
      <Card className="p-6 animate-fade-up" data-testid="pct-canali-card">
        <div className="flex items-center gap-2 mb-1">
          <Percent className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Percentuali per canale</h2>
        </div>
        <p className="text-xs text-slate-500 mb-5">Compenso del collaboratore sui ricavi, diverso per ogni canale. Un cliente collegato a un'Experience usa la % Experience; un cliente portato da un partner usa la % Partner.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {CANALI.map((c) => (
            <div key={c.key} className="grid gap-1.5">
              <Label>{c.label}</Label>
              <div className="relative">
                <Input data-testid={`pct-canale-${c.key}`} type="number" min="0" max="100" value={pctCanali[c.key] ?? ""} onChange={(e) => setPctCanali((p) => ({ ...p, [c.key]: e.target.value }))} className="pr-6 tabular-nums" />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
              </div>
            </div>
          ))}
        </div>
        <Button data-testid="save-pct-canali-button" onClick={savePctCanali} className="mt-5 rounded-full gap-1.5"><Save className="h-4 w-4" /> Salva percentuali</Button>
      </Card>
      )}

      {isTitolare && (
      <Card className="p-6 animate-fade-up" data-testid="obiettivi-bonus-card">
        <div className="flex items-center gap-2 mb-1">
          <Target className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Obiettivi & Bonus</h2>
        </div>
        <p className="text-xs text-slate-500 mb-5">Imposta il bonus (importo, traguardo, scadenza) e i target mensili di acquisizione.</p>
        <div className={`grid gap-4 mb-6 ${isRitr ? "sm:grid-cols-4" : "sm:grid-cols-3"}`}>
          <div className="grid gap-1.5"><Label>Bonus (€)</Label><Input data-testid="bonus-importo-input" type="number" value={obiettivi.importo} onChange={(e) => setObiettivi((o) => ({ ...o, importo: e.target.value }))} /></div>
          {isRitr && (
            <div className="grid gap-1.5">
              <Label>Traguardo basato su</Label>
              <Select value={obiettivi.tipo} onValueChange={(v) => setObiettivi((o) => ({ ...o, tipo: v }))}>
                <SelectTrigger data-testid="bonus-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BONUS_TIPI.map((b) => <SelectItem key={b.key} data-testid={`bonus-tipo-${b.key}`} value={b.key}>{b.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid gap-1.5"><Label>Traguardo {isRitr ? (BONUS_TIPI.find((b) => b.key === obiettivi.tipo) || BONUS_TIPI[0]).unit : "SSD"}</Label><Input data-testid="bonus-target-input" type="number" value={obiettivi.target} onChange={(e) => setObiettivi((o) => ({ ...o, target: e.target.value }))} /></div>
          <div className="grid gap-1.5"><Label>Scadenza bonus</Label><Input data-testid="bonus-scadenza-input" type="date" value={obiettivi.scadenza || ""} onChange={(e) => setObiettivi((o) => ({ ...o, scadenza: e.target.value }))} /></div>
        </div>
        <Label className="text-xs uppercase tracking-wider text-slate-400">Target mensili acquisizioni</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
          {mensili.map((m, idx) => (
            <div key={m.ym} className="grid gap-1.5">
              <Label className="text-xs">{m.label}</Label>
              <Input data-testid={`target-mensile-${m.ym}`} type="number" min="0" value={m.target} onChange={(e) => setMensili((arr) => arr.map((x, j) => (j === idx ? { ...x, target: e.target.value } : x)))} />
            </div>
          ))}
        </div>
        <Button data-testid="save-obiettivi-button" onClick={saveObiettivi} className="mt-5 rounded-full gap-1.5">
          <Save className="h-4 w-4" /> Salva obiettivi
        </Button>
      </Card>
      )}

      {isTitolare && (
      <Card className="p-6 animate-fade-up">
        <div className="flex items-center gap-2 mb-1">
          <CalendarClock className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Date di liquidazione compensi</h2>
        </div>
        <p className="text-xs text-slate-500 mb-5">3 finestre di pagamento configurabili.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="grid gap-1.5">
              <Label>Rata {i + 1}</Label>
              <Input
                data-testid={`payout-date-${i + 1}-input`}
                type="date"
                value={dates[i] || ""}
                onChange={(e) => setDates((d) => d.map((x, j) => (j === i ? e.target.value : x)))}
              />
              <span className="text-xs text-slate-400">{dateIt(dates[i])}</span>
            </div>
          ))}
        </div>
        <Button data-testid="save-payout-dates-button" onClick={saveDates} className="mt-5 rounded-full gap-1.5">
          <Save className="h-4 w-4" /> Salva date
        </Button>
      </Card>
      )}

      {isTitolare && (
      <Card className="p-6 animate-fade-up" data-testid="storico-compensi-card">
        <div className="flex items-center gap-2 mb-1">
          <History className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Storico Compensi</h2>
        </div>
        <p className="text-xs text-slate-500 mb-5">Cronologia delle modifiche alle percentuali. I clienti già chiusi mantengono la percentuale attiva al momento della chiusura.</p>
        {storico.length === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center">Nessuna modifica registrata finora.</div>
        ) : (
          <div className="space-y-2">
            {storico.map((h) => (
              <div key={h.id} data-testid={`storico-row-${h.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-800 px-4 py-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  <Badge variant="outline" className="shrink-0">{h.tipo === "globale" ? "Globale" : "Collaboratore"}</Badge>
                  <span className="font-medium text-slate-800 dark:text-slate-100 truncate">{h.collaboratore_nome || "—"}</span>
                </div>
                <div className="flex items-center gap-2 text-sm shrink-0">
                  <span className="tabular-nums text-slate-400">{h.da != null ? `${Math.round(h.da * 100)}%` : "—"}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
                  <span className="tabular-nums font-semibold text-primary">{h.a != null ? `${Math.round(h.a * 100)}%` : "—"}</span>
                  <span className="text-xs text-slate-400 ml-2 tabular-nums">{dateIt(h.at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      )}
    </div>
  );
}
